import type { QuestionFigure } from '@padcanvas/shared';
import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GRADE_OPTIONS, QUESTION_TYPES, QUESTION_TYPE_LABELS, buildQuestionDisplayText } from '@padcanvas/shared';
import { useApi } from '../api';
import { useModules, moduleGrades, PROGRAMME_FORM_OPTIONS, programmeLabel } from '../reference-data';
import type { ModuleItem } from '../reference-data/types';
import { flattenQuestionIds } from '../user-tags/types';
import { useUserTags } from '../user-tags/useUserTags';
import LatexText from '../latex/LatexText';
import QuestionFigures from '../latex/QuestionFigures';
import { Pagination, ImageViewer, IdControl } from '../components';
import { TopicSelector } from '../components/TopicSelector';
import '../user-tags/TagFilter.css';
import './QuestionPicker.css';

/** Row shape returned by `/question-list` for this picker. List-view fields
 *  come from the lightweight base; preview-modal fields (questionDiagramUrl,
 *  options, correctAnswer, solutionImageUrl) come from
 *  `?includeFields=questionDiagramUrl,options,correctAnswer,solutionImageUrl`. */
/** A question's appearance in a paper. `displayString` is the canonical label;
 *  the structured fields are the fallback. */
interface ExamAppearanceItem {
  exam: string;
  date?: string;
  session?: string;
  questionNo?: string;
  displayString?: string;
}

/** Single-string label for an appearance, e.g. "JEE.2024-01-27.Shift 1.Q12".
 *  Prefers the stored displayString; otherwise joins exam.date.session.questionNo. */
function formatExamAppearance(a: ExamAppearanceItem): string {
  if (a.displayString) return a.displayString;
  return [a.exam, a.date, a.session, a.questionNo].filter(Boolean).join('.');
}

interface QuestionItem {
  id: string;
  subject?: string;
  questionText: string;
  /** STRUCTURED sub-parts (id + statement) so list/preview show stem + parts. */
  parts?: Array<{ partId: string; statement?: string }> | null;
  difficulty: string;
  questionType: string | null;
  module: { id: string; name: string } | null;
  appearedInExams: ExamAppearanceItem[] | null;
  // Populated via includeFields — used by the preview modal
  questionDiagramUrl?: string | null;
  figures?: QuestionFigure[] | null;
  options?: { label: string; text: string; imageUrl?: string | null; isCorrect?: boolean }[] | null;
  correctAnswer?: string;
  solutionImageUrl?: string | null;
  /** Lifecycle state — returned only for admin requests (undefined otherwise),
   *  so the draft flag is admin-only by construction. */
  importStatus?: 'draft' | 'live' | null;
  /** Display marks — the single-part value, or the sum of a STRUCTURED
   *  question's parts. Opt-in (see `PICKER_INCLUDE_FIELDS`), and requested only
   *  when a paper is being marked up: student-facing lists must not leak marks. */
  marks?: number;
}

/** includeFields the picker needs to populate its preview modal. Kept as a
 *  const so the two fetch sites share the same opt-in set. */
const PICKER_INCLUDE_FIELDS = 'questionDiagramUrl,options,correctAnswer,solutionImageUrl';

/** …plus each question's marks, so the Marks column can pre-fill from them.
 *  Asked for ONLY when that column is in use — the picker also serves a student
 *  choosing their own practice, and those lists must not carry marks. */
const PICKER_INCLUDE_FIELDS_WITH_MARKS = `${PICKER_INCLUDE_FIELDS},marks`;

interface FetchParams {
  subject: string;
  moduleId: string;
  difficulty: string;
  targetExam: string;
  grade: string;
  search: string;
  /** Exact `questionType` match (MCQ, NUMERICAL, STRUCTURED, …). '' = any. */
  questionType: string;
  /** Restrict to previous-year questions. With an exam selected this narrows to
   *  appearances in that exam; otherwise it means "appeared in any real paper". */
  pyqOnly: boolean;
}

export interface QuestionPickerProps {
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  subjects?: string[];
  /** Show Public/Private source toggle. When true, user can switch between public and teacher-private questions. */
  showSourceFilter?: boolean;
  /** Map of questionId → other assigned question sets (besides the current one) that contain this question.
   *  When provided, selected questions whose id has entries here are marked as "already in another assignment". */
  usageByQuestionId?: Record<string, { questionSetId: string; questionSetTitle: string }[]>;
  /** Seed the Subject / Module / Difficulty filters (e.g. from a Topic Exercise's
   *  anchor) so the admin doesn't re-enter them. Re-applied when these change; the
   *  admin can still override the filters afterwards. `initialModuleId` uses the
   *  TopicSelector value form ('top:<id>' for a whole module, '<id>' for a chapter). */
  initialSubject?: string;
  initialModuleId?: string;
  initialDifficulty?: string;
  /** Show the "…" control that opens the full question, answer and solution.
   *  Defaults on for the teacher and admin consoles; a student picking their own
   *  practice must not be able to read the answer while choosing. */
  showPreview?: boolean;
  /** Fix the class and hide its control — the caller already knows it (a student
   *  browsing their own year). Also narrows the chapter/topic list to that class,
   *  so the picker never offers a topic the student cannot be asked. */
  lockedGrade?: string;
  /** Which exams the user actually studies for, from their profile. One exam
   *  means there is nothing to choose: it is applied silently and the control is
   *  hidden. Omitted ⇒ the full list, as before. */
  targetExamOptions?: string[];
  /**
   * What each selected question is worth ON THIS PAPER — questionId → marks.
   *
   * Present ⇒ a Marks column is offered on the selected list, pre-filled with
   * the question's own marks. A key here is an OVERRIDE; clearing the box drops
   * the key rather than storing 0, so "use the question's marks" and "worth
   * nothing on this paper" stay different answers.
   *
   * Omitted ⇒ no column, which is right for the surfaces that are not composing
   * a marked paper (a student picking practice).
   * See docs/FeatureDesigns/assignments-tests/per-test-question-marks-target.md.
   */
  questionMarks?: Record<string, number>;
  onQuestionMarksChange?: (marks: Record<string, number>) => void;
  /** The chapter list to offer instead of the module tree, by subject (lower
   *  case) — a student's own curriculum, so the picker reads like Practice.
   *  `scopes` maps each entry to the module ids its questions are filed under;
   *  a pick is fetched as exactly that set. A subject missing here falls back to
   *  the module tree. */
  curriculumTopics?: Record<string, CurriculumTopics>;
}

export interface CurriculumTopics {
  topics: ModuleItem[];
  scopes: Record<string, string[]>;
}

export function QuestionPicker({ selectedIds, onSelectionChange, subjects: allowedSubjects, showSourceFilter, usageByQuestionId, initialSubject, initialModuleId, initialDifficulty, lockedGrade, targetExamOptions, showPreview = true, questionMarks, onQuestionMarksChange, curriculumTopics }: QuestionPickerProps) {
  const { fetchApi } = useApi();
  // The Marks column is offered only where the caller keeps the map — the
  // surfaces composing a marked paper.
  const marksEditable = !!questionMarks && !!onQuestionMarksChange;
  const includeFields = marksEditable ? PICKER_INCLUDE_FIELDS_WITH_MARKS : PICKER_INCLUDE_FIELDS;
  const { modules: allModules } = useModules();
  const [subject, setSubject] = useState(initialSubject ?? '');
  const [difficulty, setDifficulty] = useState(initialDifficulty ?? '');
  // A single exam is the user's only answer, so it starts applied rather than
  // as "All Exams" the user has to set.
  const [targetExam, setTargetExam] = useState(
    targetExamOptions?.length === 1 ? targetExamOptions[0] : '',
  );
  const [grade, setGrade] = useState(lockedGrade ?? '');
  const [sourceFilter, setSourceFilter] = useState<'' | 'private'>('');
  const [filterModule, setFilterModule] = useState(initialModuleId ?? '');
  const [search, setSearch] = useState('');
  const [questionType, setQuestionType] = useState('');
  const [pyqOnly, setPyqOnly] = useState(false);

  // Re-seed the filters when the caller's initial values change (e.g. the admin
  // edits a Topic Exercise's subject/anchor/difficulty). The admin can still
  // override the filters afterwards — this only fires on an actual initial change.
  useEffect(() => { if (initialSubject !== undefined) setSubject(initialSubject); }, [initialSubject]);
  useEffect(() => { if (initialModuleId !== undefined) setFilterModule(initialModuleId); }, [initialModuleId]);
  useEffect(() => { if (initialDifficulty !== undefined) setDifficulty(initialDifficulty); }, [initialDifficulty]);
  const [previewQuestion, setPreviewQuestion] = useState<QuestionItem | null>(null);
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const PAGE_SIZE = 100;

  // Track the filters that were actually used for the last fetch.
  // null = no fetch yet (user hasn't clicked Fetch)
  const [fetchParams, setFetchParams] = useState<FetchParams | null>(null);
  const [fetchSource, setFetchSource] = useState<'' | 'private'>('');
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['browse-questions', fetchParams?.subject, fetchParams?.moduleId, fetchParams?.difficulty, fetchParams?.targetExam, fetchParams?.grade, fetchParams?.search, fetchParams?.questionType, fetchParams?.pyqOnly, fetchSource, page],
    queryFn: () => {
      const params = new URLSearchParams();
      if (fetchParams!.subject) params.append('subject', fetchParams!.subject);
      if (fetchParams!.moduleId) params.append('moduleId', fetchParams!.moduleId);
      if (fetchParams!.difficulty) params.append('difficulty', fetchParams!.difficulty);
      if (fetchParams!.targetExam) params.append('targetExam', fetchParams!.targetExam);
      if (fetchParams!.grade) params.append('grade', fetchParams!.grade);
      if (fetchParams!.search) params.append('searchString', fetchParams!.search);
      if (fetchParams!.questionType) params.append('questionType', fetchParams!.questionType);
      // With an exam chosen, "PYQ" means "appeared in THAT exam" — the precise
      // filter. Without one it falls back to the exam-agnostic flag.
      if (fetchParams!.pyqOnly) {
        if (fetchParams!.targetExam) params.append('appearedInExam', fetchParams!.targetExam);
        else params.append('pyqOnly', 'true');
      }
      if (fetchSource === 'private') params.append('accessCategory', 'teacher-allstudents');
      // Composition is live-only. Admins (and teachers on their private bank)
      // otherwise see drafts here, because the server's default status scope is
      // per-role — right for the browse screens, wrong for picking questions
      // into a test or assignment. Pinning it here covers every picker caller.
      params.append('importStatus', 'live');
      params.append('pageSize', String(PAGE_SIZE));
      params.append('page', String(page));
      params.append('includeFields', includeFields);
      const qs = params.toString();
      return fetchApi<{ questions: QuestionItem[]; total: number }>(`/question-list${qs ? `?${qs}` : ''}`);
    },
    enabled: fetchParams !== null,
  });

  // Fetch user tags for client-side filtering & display
  const { visibleTags } = useUserTags();

  // Build tag -> questionId set mapping
  const tagQuestionSets = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const t of visibleTags) {
      map.set(t.tag, flattenQuestionIds(t.questions));
    }
    return map;
  }, [visibleTags]);

  // Build reverse map: questionId -> tag names (for display)
  const questionTagNames = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const [tag, ids] of tagQuestionSets) {
      for (const id of ids) {
        if (!map.has(id)) map.set(id, []);
        map.get(id)!.push(tag);
      }
    }
    return map;
  }, [tagQuestionSets]);

  const allTagNames = useMemo(() => [...(tagQuestionSets.keys())].sort(), [tagQuestionSets]);

  const rawQuestions = data?.questions || [];

  // Client-side tag filter
  const questions = useMemo(() => {
    if (selectedTags.size === 0) return rawQuestions;
    return rawQuestions.filter(q => {
      for (const tag of selectedTags) {
        if (tagQuestionSets.get(tag)?.has(q.id)) return true;
      }
      return false;
    });
  }, [rawQuestions, selectedTags, tagQuestionSets]);

  const total = selectedTags.size > 0 ? questions.length : (data?.total ?? null);
  const totalPages = selectedTags.size > 0 ? null : (total !== null ? Math.ceil(total / PAGE_SIZE) : null);

  const curriculum = subject ? curriculumTopics?.[subject.toLowerCase()] : undefined;

  const handleFetchQuestions = () => {
    // A curriculum entry is a book placement, not a module: send the exact
    // module ids it covers (the same set Practice lists for it). The book has
    // already chosen the class, so the class filter is dropped as Practice drops
    // it — questions tagged for another year (a Class 10 JEE student's chapters
    // hold Class 11–12 questions) would otherwise empty the list.
    const scope = curriculum && filterModule ? curriculum.scopes[filterModule] : undefined;
    const moduleIdParam = scope ? `ids:${scope.join(',')}`
      : filterModule.startsWith('top:') ? filterModule.slice(4) : filterModule;
    setPage(1);
    setFetchSource(sourceFilter);
    setFetchParams({ subject, moduleId: moduleIdParam, difficulty, targetExam, grade: scope ? '' : grade, search: search.trim(), questionType, pyqOnly });
  };

  // Derive subjects from the full modules list (stable, not dependent on question fetch)
  const subjects = useMemo(() => {
    const getLevel = (m: { isTopModule: boolean; moduleLevel?: string }) => m.moduleLevel ?? (m.isTopModule ? 'module' : 'chapter');
    const s = new Set(allModules.filter(m => getLevel(m) === 'module').map(m => m.subject));
    let list = [...s];
    if (allowedSubjects?.length) {
      const allowed = new Set(allowedSubjects.map(v => v.toLowerCase()));
      list = list.filter(v => allowed.has(v.toLowerCase()));
    }
    return list.sort();
  }, [allModules, allowedSubjects]);

  // Auto-select if only one subject and trigger initial fetch
  const singleSubject = subjects.length === 1;
  useEffect(() => {
    if (singleSubject && !subject) {
      setSubject(subjects[0]);
      setFetchParams({ subject: subjects[0], moduleId: '', difficulty: '', targetExam: '', grade: '', search: '', questionType: '', pyqOnly: false });
    }
  }, [singleSubject, subjects, subject]);

  // Reset module filter when subject changes
  const handleSubjectChange = (newSubject: string) => {
    setSubject(newSubject);
    setFilterModule('');
  };

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // Persistent cache: accumulate question data across fetches so selected questions
  // remain visible even when filters change and the current fetch no longer includes them.
  const [seenQuestions, setSeenQuestions] = useState<Map<string, QuestionItem>>(new Map());
  useEffect(() => {
    if (questions.length === 0) return;
    setSeenQuestions(prev => {
      const next = new Map(prev);
      let changed = false;
      for (const q of questions) {
        if (!next.has(q.id)) { next.set(q.id, q); changed = true; }
      }
      return changed ? next : prev;
    });
  }, [questions]);

  // Fetch details for pre-selected questions not yet in cache (e.g. editing an existing test)
  useEffect(() => {
    const missing = selectedIds.filter(id => !seenQuestions.has(id));
    if (missing.length === 0) return;
    // Fetch in batches of 100
    (async () => {
      for (let i = 0; i < missing.length; i += 100) {
        const batch = missing.slice(i, i + 100);
        const params = new URLSearchParams();
        params.append('ids', batch.join(','));
        params.append('pageSize', '100');
        params.append('includeFields', includeFields);
        try {
          const result = await fetchApi<{ questions: QuestionItem[] }>(`/question-list?${params}`);
          if (result.questions.length > 0) {
            setSeenQuestions(prev => {
              const next = new Map(prev);
              for (const q of result.questions) next.set(q.id, q);
              return next;
            });
          }
        } catch { /* ignore — fallback to ID display */ }
      }
    })();
  }, [selectedIds.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleQuestion = (q: QuestionItem) => {
    if (selectedSet.has(q.id)) {
      onSelectionChange(selectedIds.filter(id => id !== q.id));
    } else {
      onSelectionChange([...selectedIds, q.id]);
    }
  };

  const removeQuestion = (id: string) => {
    onSelectionChange(selectedIds.filter(qid => qid !== id));
  };

  // Reorder a selected question — the order is the test/paper order, so admins
  // can nudge questions up/down. `dir` is -1 (up) or +1 (down); no-op at edges.
  const moveQuestion = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= selectedIds.length) return;
    const next = [...selectedIds];
    [next[idx], next[target]] = [next[target], next[idx]];
    onSelectionChange(next);
  };

  // Sort the selection by appeared-in-exam value. Numeric-aware so "…Shift 1.3"
  // precedes "…Shift 1.10"; questions without an appearance sort to the end.
  const sortByAppearance = () => {
    const keyFor = (id: string) => {
      const a = seenQuestions.get(id)?.appearedInExams?.[0];
      return a ? formatExamAppearance(a) : '';
    };
    const sorted = [...selectedIds].sort((x, y) => {
      const kx = keyFor(x), ky = keyFor(y);
      if (kx === ky) return 0;
      if (!kx) return 1;
      if (!ky) return -1;
      return kx.localeCompare(ky, undefined, { numeric: true, sensitivity: 'base' });
    });
    onSelectionChange(sorted);
  };

  const addAll = () => {
    const newIds = questions.filter(q => !selectedSet.has(q.id)).map(q => q.id);
    onSelectionChange([...selectedIds, ...newIds]);
  };

  /** How many "pick random" grabs at once — a round number for a quick test. */
  const RANDOM_PICK_COUNT = 10;

  /** Questions on this page the student hasn't already picked. */
  const unpickedCount = questions.filter(q => !selectedSet.has(q.id)).length;

  // Ten unpicked questions from the page, at random. Fisher–Yates on a copy:
  // `sort(() => Math.random() - 0.5)` is the usual shortcut and is not a
  // uniform shuffle — its comparator is inconsistent, so some questions come up
  // far more often than others.
  const addRandom = () => {
    const pool = questions.filter(q => !selectedSet.has(q.id)).map(q => q.id);
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    onSelectionChange([...selectedIds, ...pool.slice(0, RANDOM_PICK_COUNT)]);
  };

  const hasFetched = fetchParams !== null;

  // ── Per-paper marks ─────────────────────────────────────────────────────
  /** What this question is worth on THIS paper: the override when the paper set
   *  one, otherwise the question's own marks. Mirrors `worthOnPaper` server-side,
   *  `??` included, so an override of 0 is honoured rather than fallen through. */
  const worthOf = (id: string): number | undefined =>
    questionMarks?.[id] ?? seenQuestions.get(id)?.marks;

  /** Only once every question's worth is known. A total that silently counts
   *  nine of fifteen is worse than none: it looks like an answer. */
  const paperTotal = marksEditable && selectedIds.length > 0
    && selectedIds.every(id => worthOf(id) != null)
    ? selectedIds.reduce((sum, id) => sum + (worthOf(id) ?? 0), 0)
    : null;

  /** An empty box DROPS the key, so the question's own marks answer again. A
   *  stored 0 means "worth nothing on this paper" — a different thing, and one
   *  someone may deliberately want. */
  const setWorth = (id: string, raw: string) => {
    if (!questionMarks || !onQuestionMarksChange) return;
    const next = { ...questionMarks };
    const trimmed = raw.trim();
    if (trimmed === '') delete next[id];
    else {
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n < 0) return;
      next[id] = Math.round(n);
    }
    onQuestionMarksChange(next);
  };

  return (
    <div className="question-picker">
      <div className="qp-filters">
        {showSourceFilter && (
          <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value as '' | 'private')}>
            <option value="">Public</option>
            <option value="private">Private</option>
          </select>
        )}
        {!singleSubject && (
          <select value={subject} onChange={e => handleSubjectChange(e.target.value)}>
            <option value="">All Subjects</option>
            {subjects.map(s => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
        )}
        {!lockedGrade && (
          <select value={grade} onChange={e => setGrade(e.target.value)}>
            <option value="">All Classes</option>
            {GRADE_OPTIONS.map(c => (
              <option key={c} value={c}>Class {c}</option>
            ))}
          </select>
        )}
        <TopicSelector
          // Narrowed by class as well as subject: offering a chapter from
          // another year is offering questions the fetch will not return.
          // Grade scoping goes through the shared helper so this reads the same
          // record everything else does — it was the one caller consulting
          // `grades` alone, which silently ignored a module whose grade is only
          // recorded per board. Modules claiming no grade are kept: absence
          // means "any", not "none".
          modules={curriculum?.topics ?? allModules.filter(m =>
            (!subject || m.subject === subject)
            && (!grade || !moduleGrades(m).length || moduleGrades(m).includes(Number(grade))))}
          preserveOrder={!!curriculum}
          value={filterModule}
          onChange={v => setFilterModule(v)}
          placeholder="Chapter / Topic..."
          disabled={!subject}
        />
        <select value={difficulty} onChange={e => setDifficulty(e.target.value)}>
          <option value="">All Difficulty</option>
          <option value="easy">Easy</option>
          <option value="medium">Medium</option>
          <option value="hard">Hard</option>
        </select>
        {/* Hidden when the user studies for exactly one exam — the value is
            already applied, and a one-item dropdown is a decision nobody has. */}
        {targetExamOptions?.length !== 1 && (
          <select value={targetExam} onChange={e => setTargetExam(e.target.value)}>
            <option value="">All Exams</option>
            {(targetExamOptions?.length
              ? targetExamOptions
              : PROGRAMME_FORM_OPTIONS.map(o => o.value)
            ).map(v => (
              <option key={v} value={v}>{programmeLabel(v)}</option>
            ))}
          </select>
        )}
        <select value={questionType} onChange={e => setQuestionType(e.target.value)}>
          <option value="">All Types</option>
          {QUESTION_TYPES.map(t => (
            <option key={t} value={t}>{QUESTION_TYPE_LABELS[t]}</option>
          ))}
        </select>
        <label
          className="qp-pyq-toggle"
          title={targetExam
            ? `Only questions that appeared in a ${targetExam} paper`
            : 'Only previous-year questions (appeared in a real exam paper)'}
        >
          <input
            type="checkbox"
            checked={pyqOnly}
            onChange={e => setPyqOnly(e.target.checked)}
          />
          PYQ only
        </label>
        <input
          type="text"
          placeholder="Search in results..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="qp-search"
        />
        <button
          type="button"
          className="qp-refresh-btn"
          onClick={handleFetchQuestions}
          disabled={isLoading || isFetching}
          title="Fetch questions with current filters"
        >
          {isLoading || isFetching ? '...' : 'Fetch'}
        </button>
      </div>

      {/* Tag pills for client-side filtering */}
      {allTagNames.length > 0 && (
        <div className="qtag-filter-pills">
          {allTagNames.map(tag => {
            const active = selectedTags.has(tag);
            const count = tagQuestionSets.get(tag)?.size ?? 0;
            return (
              <button
                key={tag}
                type="button"
                className={`qtag-filter-pill${active ? ' qtag-filter-pill-active' : ''}`}
                onClick={() => {
                  setSelectedTags(prev => {
                    const next = new Set(prev);
                    if (next.has(tag)) next.delete(tag);
                    else next.add(tag);
                    return next;
                  });
                }}
              >
                {tag} <span className="qtag-filter-pill-count">{count}</span>
              </button>
            );
          })}
          {selectedTags.size > 0 && (
            <button type="button" className="qtag-filter-clear" onClick={() => setSelectedTags(new Set())}>
              Clear tags
            </button>
          )}
        </div>
      )}

      {/* Browse questions — full width list */}
      <div className="qp-browse">
        <div className="qp-panel-header">
          <h4>
            {total !== null && total > 0 ? (
              <>Questions ({(page - 1) * PAGE_SIZE + 1}–{(page - 1) * PAGE_SIZE + questions.length}) <span className="qp-total-hint">of {total}</span></>
            ) : (
              <>Questions ({questions.length})</>
            )}
          </h4>
          {totalPages !== null && totalPages > 1 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              disabled={isFetching}
            />
          )}
          {/* Wrapped in one div rather than a fragment: the header is
              space-between, so two bare children would end up at opposite ends
              of the row. */}
          {unpickedCount > 0 && (
            <div className="qp-panel-actions">
              {/* Before "Add all": the common want is a handful to practise on,
                  not the whole page. Hidden once there are ten or fewer left to
                  pick — "10 random" out of nine is just "add all", the long way
                  round. */}
              {unpickedCount > RANDOM_PICK_COUNT && (
                <button type="button" className="qp-btn-link" onClick={addRandom}>
                  Add {RANDOM_PICK_COUNT} random
                </button>
              )}
              <button type="button" className="qp-btn-link" onClick={addAll}>Add all{totalPages && totalPages > 1 ? ' on page' : ''}</button>
            </div>
          )}
        </div>
        <div className="qp-list">
          {!hasFetched ? (
            <p className="qp-empty">Select filters and click Fetch to load questions</p>
          ) : isLoading ? (
            <p className="qp-empty">Loading questions...</p>
          ) : questions.length === 0 ? (
            <p className="qp-empty">No questions found</p>
          ) : (
            questions.map(q => {
              const isSelected = selectedSet.has(q.id);
              return (
                <div
                  key={q.id}
                  className={`qp-item${isSelected ? ' qp-item-highlighted' : ''}`}
                  onClick={() => toggleQuestion(q)}
                >
                  <div className="qp-item-check">
                    {isSelected ? '\u2713' : ''}
                  </div>
                  <div className="qp-item-body">
                    <div className="qp-item-text"><LatexText text={buildQuestionDisplayText(q)} truncate={160} expandInline /></div>
                    <div className="qp-item-meta">
                      {q.importStatus === 'draft' && <span className="qp-badge-draft">Draft</span>}
                      <span className="qp-badge">{q.difficulty}</span>
                      {q.module?.name && <span className="qp-badge-muted">{q.module.name}</span>}
                      {q.questionType && q.questionType !== 'text' && <span className="qp-badge-muted">{q.questionType}</span>}
                      {q.appearedInExams?.map((e, i) => (
                        <span key={i} className="qp-badge-exam">{e.exam}{e.date ? ` ${new Date(e.date).getFullYear()}` : ''}</span>
                      ))}
                      {questionTagNames.get(q.id)?.map(tag => (
                        <span key={tag} className="qtag-inline">{tag}</span>
                      ))}
                    </div>
                  </div>
                  {showPreview && (
                    <button
                      type="button"
                      className="qp-preview-btn"
                      onClick={(e) => { e.stopPropagation(); setPreviewQuestion(q); }}
                      title="Preview full question"
                    >
                      &hellip;
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Selected questions list */}
      <div className="qp-selected">
        <div className="qp-panel-header">
          <h4>
            Selected ({selectedIds.length})
            {marksEditable && paperTotal !== null && (
              /* A paper's total is its most-checked number, and the editor is
                 where a mistake in it is cheap to fix. Only once every question
                 has resolved — a total that counts nine of fifteen is worse than
                 no total, because it looks like an answer. */
              <span className="qp-marks-total"> · {paperTotal} marks</span>
            )}
          </h4>
          {selectedIds.length > 0 && (
            <div className="qp-panel-actions">
              {selectedIds.length > 1 && (
                <button type="button" className="qp-btn-link" onClick={sortByAppearance} title="Reorder by appeared-in-exam value">Sort by exam</button>
              )}
              <button type="button" className="qp-btn-link" onClick={() => onSelectionChange([])}>Clear all</button>
            </div>
          )}
        </div>
        <div className="qp-selected-list">
          {selectedIds.length === 0 ? (
            <p className="qp-empty">No questions selected</p>
          ) : (
            selectedIds.map((id, idx) => {
              const q = seenQuestions.get(id);
              const usedIn = usageByQuestionId?.[id];
              const isDraft = q?.importStatus === 'draft';
              return (
                <div key={id} className={`qp-selected-item${usedIn ? ' usage-row-accent' : ''}${isDraft ? ' qp-selected-item-draft' : ''}`}>
                  <span className="qp-item-num">{idx + 1}.</span>
                  <div className="qp-item-text">
                    {q ? <LatexText text={buildQuestionDisplayText(q)} truncate={120} expandInline /> : <span className="qp-id-fallback">{id}</span>}
                    {isDraft && <span className="qp-badge-draft" style={{ marginLeft: 8 }}>Draft</span>}
                    {usedIn && usedIn.length > 0 && (
                      <span
                        className="usage-pill"
                        style={{ marginLeft: 8 }}
                        title={`Already in: ${usedIn.map(u => u.questionSetTitle).join(', ')}`}
                      >
                        in {usedIn.length === 1 ? usedIn[0].questionSetTitle : `${usedIn.length} other assignments`}
                      </span>
                    )}
                  </div>
                  <span className="qp-col-type">
                    {q?.questionType && q.questionType !== 'text' && (
                      <span className="qp-badge-muted">{q.questionType}</span>
                    )}
                  </span>
                  {marksEditable && (
                    /* Pre-filled with the question's own marks, which are usually
                       right — the teacher edits the exceptions rather than filling
                       in a column. Empty means "use the question's", which is why
                       clearing it drops the key instead of storing 0. */
                    <span className="qp-col-marks">
                      <input
                        type="number"
                        min={0}
                        step={1}
                        className="qp-marks-input"
                        value={worthOf(id) ?? ''}
                        placeholder="—"
                        aria-label={`Marks for question ${idx + 1} on this paper`}
                        title={questionMarks?.[id] != null
                          ? 'Worth on this paper. Clear the box to use the question’s own marks.'
                          : 'The question’s own marks. Type a number to change what it is worth on this paper.'}
                        onChange={e => setWorth(id, e.target.value)}
                      />
                    </span>
                  )}
                  <span
                    className="qp-col-appearance"
                    title={q?.appearedInExams?.map(formatExamAppearance).join('\n')}
                  >
                    {q?.appearedInExams && q.appearedInExams.length > 0 && (
                      <>
                        {formatExamAppearance(q.appearedInExams[0])}
                        {q.appearedInExams.length > 1 ? ` +${q.appearedInExams.length - 1}` : ''}
                      </>
                    )}
                  </span>
                  {showPreview && q && (
                    <button
                      type="button"
                      className="qp-preview-btn"
                      onClick={() => setPreviewQuestion(q)}
                      title="Preview full question"
                    >
                      &hellip;
                    </button>
                  )}
                  <span className="qp-reorder">
                    <button
                      type="button"
                      className="qp-reorder-btn"
                      onClick={() => moveQuestion(idx, -1)}
                      disabled={idx === 0}
                      title="Move up"
                    >
                      &uarr;
                    </button>
                    <button
                      type="button"
                      className="qp-reorder-btn"
                      onClick={() => moveQuestion(idx, 1)}
                      disabled={idx === selectedIds.length - 1}
                      title="Move down"
                    >
                      &darr;
                    </button>
                  </span>
                  <button type="button" className="qp-remove" onClick={() => removeQuestion(id)} title="Remove">&times;</button>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Question preview modal */}
      {previewQuestion && (
        <div className="qp-modal-overlay" onClick={() => setPreviewQuestion(null)}>
          <div className="qp-modal" onClick={e => e.stopPropagation()}>
            <div className="qp-modal-header">
              <h3>Question Preview</h3>
              <button type="button" className="qp-modal-close" onClick={() => setPreviewQuestion(null)}>&times;</button>
            </div>
            <div className="qp-modal-body">
              <div className="qp-item-meta" style={{ marginBottom: '12px' }}>
                <span className="qp-badge">{previewQuestion.subject}</span>
                <span className="qp-badge">{previewQuestion.difficulty}</span>
                {previewQuestion.questionType && <span className="qp-badge-muted">{previewQuestion.questionType}</span>}
                {previewQuestion.module?.name && <span className="qp-badge-muted">{previewQuestion.module.name}</span>}
                <IdControl value={previewQuestion.id} container="questions" />
              </div>
              <div className="qp-preview-text"><LatexText text={buildQuestionDisplayText(previewQuestion)} figures={previewQuestion.figures} /></div>
              {previewQuestion.questionDiagramUrl && (
                <ImageViewer src={previewQuestion.questionDiagramUrl} alt="Question" className="qp-preview-image" />
              )}
              <QuestionFigures text={previewQuestion.questionText} figures={previewQuestion.figures} className="qp-preview-image" />
              {previewQuestion.options && previewQuestion.options.length > 0 && (
                <div className="qp-preview-options">
                  {previewQuestion.options.map((opt, i) => (
                    <div key={i} className="qp-preview-option">
                      <span className="qp-option-label">{opt.label.toUpperCase()}.</span>
                      <span><LatexText text={opt.text} /></span>
                      {opt.imageUrl && <ImageViewer src={opt.imageUrl} alt={opt.label} className="qp-option-image" />}
                    </div>
                  ))}
                </div>
              )}
              <div className="qp-preview-answer">
                <strong>Answer:</strong> <LatexText text={previewQuestion.correctAnswer ?? ''} />
              </div>
              {previewQuestion.solutionImageUrl && (
                <div className="qp-preview-solution">
                  <strong>Solution:</strong>
                  <ImageViewer src={previewQuestion.solutionImageUrl} alt="Solution" className="qp-preview-image" />
                </div>
              )}
            </div>
            <div className="qp-modal-footer">
              <button
                type="button"
                className={`qp-refresh-btn${selectedSet.has(previewQuestion.id) ? ' qp-btn-remove' : ''}`}
                onClick={() => {
                  toggleQuestion(previewQuestion);
                }}
              >
                {selectedSet.has(previewQuestion.id) ? 'Remove from Selection' : 'Add to Selection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
